import { useTranslation } from 'react-i18next';
import { useSearchPanelDoctor } from 'all-in-one-shared-api';
import { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import CustomGoogleMap from '../GoogleMap';
import styled from 'styled-components';
import { NetworkList } from '../NetworkList';
import { MapDataUtils } from '../../utils/mapData';
import { FullWidthSpace } from 'components';
import Filters from '../filters/Filters';
import { InputRef, Result } from 'antd';
import { breakpoint } from 'styled-components-breakpoint';
import { useBookmarkContext } from 'contexts/bookmark';
import { HomeContext } from 'contexts/home/context';
import useCoordinates, { CENTRAL_COORDS } from '../../hooks/useCoordinates';
import { ALL_AREA, ALL_DISTRICTS } from 'constants/areaNDistricts';

const ListItemNMapWrapper = styled.div`
  display: grid;
  ${breakpoint('tablet')`
    grid-template-columns: 425px 2fr;
    grid-column-gap: 24px;
  `}
`;

type DoctorTabPaneProp = {
  inputEl: RefObject<InputRef>;
  wrapperRef?: RefObject<HTMLDivElement>;
};

export const DoctorTabPane = ({ inputEl, wrapperRef }: DoctorTabPaneProp): JSX.Element => {
  const { t } = useTranslation();
  const { bookmark } = useBookmarkContext();
  const [searchQuery, setSearchQuery] = useState<string>(inputEl?.current?.input?.value ?? '');
  const [specialties, setSpecialties] = useState<string[]>(['General Practitioner'.toUpperCase()]);
  const { coords } = useCoordinates();
  const [area, setArea] = useState<string | undefined>(ALL_AREA);
  const [districts, setDistricts] = useState<string[] | undefined>([ALL_DISTRICTS]);
  const { ready, token } = useKeycloakReady();
  const [googleMapCoords, setGoogleMapCoords] = useState<google.maps.LatLngLiteral | undefined>({
    lat: CENTRAL_COORDS.latitude,
    lng: CENTRAL_COORDS.longitude,
  });
  const [page, setPage] = useState<number>(1);
  const [currentItemId, setCurrentItemId] = useState<number | string | undefined>(undefined);
  const { state } = useContext(HomeContext);
  const { userAccess } = state;
  const isTeladoc = !!userAccess?.teladoc;
  const PAGE_SIZE = isTeladoc ? 4 : 5;

  const allDistrictsSelected = useMemo(() => {
    return districts?.length && districts.includes(ALL_DISTRICTS);
  }, [districts]);
  const shouldUseCoordinates = useMemo(() => {
    if (area === ALL_AREA || area === '') return false;
    return allDistrictsSelected;
  }, [allDistrictsSelected, area]);
  const allSpecialtiesSelected = useMemo(() => specialties.includes('ALL'), [specialties]);
  const { data, shouldFetch, mutate } = useSearchPanelDoctor(
    {
      name: 'searchDoctor',
      params: {
        searchQuery,
        specialties,
        area,
        districts,
        page,
      },
    },
    token || '',
    shouldUseCoordinates ? coords?.latitude : undefined,
    shouldUseCoordinates ? coords?.longitude : undefined,
    coords?.radius,
    searchQuery,
    allSpecialtiesSelected ? undefined : specialties,
    allDistrictsSelected ? undefined : area,
    allDistrictsSelected ? undefined : districts,
    page - 1,
    PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, districts, specialties]);

  useEffect(() => {
    wrapperRef?.current?.scrollIntoView();
  }, [page, wrapperRef]);

  // coords change upon GPS is ON/OFF
  useEffect(() => {
    setGoogleMapCoords({ lat: coords?.latitude ?? 0, lng: coords?.longitude ?? 0 });
    if (ready) {
      shouldFetch(true);
      mutate();
    }
  }, [ready, mutate, coords, shouldFetch]);

  const mappedData = useMemo(() => {
    return (data?.results || []).map((a) => {
      const bm = bookmark?.find((m) => m.data?.doctorClinicId === a.doctorClinicId);
      return MapDataUtils.mapData(a, bm?.bookmarkId);
    });
  }, [bookmark, data]);

  useEffect(() => {
    if (mappedData.length) {
      const currentItem = mappedData[0];
      setCurrentItemId(currentItem.itemId);
      setGoogleMapCoords({
        lat: currentItem.latitude ?? 0,
        lng: currentItem.longitude ?? 0,
      });
    }
  }, [mappedData, coords]);

  const handleOnListItemSelected = (rowId: number | string, latitude: number, longitude: number) => {
    setCurrentItemId(rowId);
    setGoogleMapCoords({ lat: latitude, lng: longitude });
  };

  return (
    <FullWidthSpace direction="vertical" size="middle">
      <Filters
        specialtyValue={specialties}
        handleOnSpecialtyChange={setSpecialties}
        districtValue={districts}
        handleOnDistrictChange={setDistricts}
        handleOnAreaChange={setArea}
        handleOnSearchQueryChange={setSearchQuery}
        showDistrict={true}
        showSpecialty={true}
        onlyGeneralPractitioner={true}
        inputEl={inputEl}
      />
      {data?.totalCount ? (
        <ListItemNMapWrapper>
          <NetworkList
            setPage={setPage}
            page={page}
            pageSize={PAGE_SIZE}
            total={data?.totalCount}
            items={mappedData}
            handleOnListItemSelected={handleOnListItemSelected}
            currentRowId={currentItemId}
            isShowTeladoc={isTeladoc}
            searchQuery={searchQuery}
          />
          <div>
            <CustomGoogleMap location={googleMapCoords} data={mappedData} handleOnMarkerClick={setCurrentItemId} />
          </div>
        </ListItemNMapWrapper>
      ) : (
        <Result icon={<></>} title={t('panelList.noResults')} subTitle={t('panelList.tryFiltering')} />
      )}
    </FullWidthSpace>
  );
};
